import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, Button, VStack, Heading, useToast, Flex, 
  Text, Icon, useColorModeValue
} from '@chakra-ui/react';
import ReactSelect from 'react-select';
import { MdVideoCall } from 'react-icons/md';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';

export default function MeetingManager() {
  const [loading, setLoading] = useState(false);
  const [createdMeetingInfo, setCreatedMeetingInfo] = useState(null);
  const [whatsappData, setWhatsappData] = useState([]);
  const [selectedMatiere, setSelectedMatiere] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  
  const toast = useToast();
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const headingColor = useColorModeValue("gray.700", "white");
  const textColor = useColorModeValue("gray.500", "gray.400");
  const inputBg = useColorModeValue("gray.50", "gray.900");
  const { user } = useContext(AuthContext);

  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await axiosInstance.get('/attendance/whatsapp-groups');
        let data = response.data;
        // If instructor, filter the whatsappData matieres to only what they are assigned to
        if (user?.role === 'instructor' && user.matiers && user.matiers.length > 0) {
          data = data.filter(item => user.matiers.includes(item.matier));
        }
        setWhatsappData(data);
      } catch (err) {
        console.error("Error fetching whatsapp groups:", err);
      }
    };
    
    const fetchAllCourses = async () => {
      try {
        const res = await axiosInstance.get('/courses');
        const data = res.data;
        const list = Array.isArray(data) ? data : (data.data || data.courses || []);
        setCourses(list.map(c => c.name));
      } catch (err) {
        console.error("Error fetching courses:", err);
      }
    };

    fetchGroups();
    fetchAllCourses();
  }, [user]);

  const handleCreateMeeting = async () => {
    const currentGroups = whatsappData.find(item => item.matier === selectedMatiere)?.groups || [];
    const hasGroups = currentGroups.length > 0;

    if (!selectedMatiere) {
      toast({ title: "الرجاء اختيار المادة", status: "warning", duration: 3000, position: "top" });
      return;
    }

    if (hasGroups && selectedGroups.length === 0) {
      toast({ title: "الرجاء اختيار مجموعة واحدة على الأقل", status: "warning", duration: 3000, position: "top" });
      return;
    }

    const todayDate = new Date().toISOString().split('T')[0];
    const generatedRoomName = `${selectedMatiere}${selectedGroups.length > 0 ? ` - ${selectedGroups.map(g => g.value).join(', ')}` : ''} - ${todayDate}`;

    setLoading(true);
    try {
      const response = await axiosInstance.post(`/meetings/create`, { 
        roomName: generatedRoomName,
        matiere: selectedMatiere,
        groups: selectedGroups.map(g => g.value)
      });
      if (response.data.success) {
        toast({
          title: "تم إنشاء الحصة بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setCreatedMeetingInfo(response.data.data);
        setSelectedMatiere("");
        setSelectedGroups([]);
      }
    } catch (err) {
      toast({
        title: "خطأ في إنشاء الحصة",
        description: err.response?.data?.error || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  const currentGroups = whatsappData.find(item => item.matier === selectedMatiere)?.groups || [];
  const matieresList = user?.role === 'instructor'
    ? (user.matiers || [])
    : courses;

  console.log("Debug - User Role:", user?.role);
  console.log("Debug - User Matiers:", user?.matiers);
  console.log("Debug - Final MatieresList:", matieresList);

  return (
    <Flex 
      minH="100vh" 
      w="100%" 
      align="center" 
      justify="center" 
      bg={useColorModeValue('gray.50', 'gray.900')}
      position="relative"
      overflow="hidden"
      p={4}
    >
      {/* Background decorations */}
      <Box position="absolute" top="-10%" left="-10%" w="40vw" h="40vw" bg="blue.400" opacity="0.15" rounded="full" filter="blur(100px)" />
      <Box position="absolute" bottom="-10%" right="-10%" w="40vw" h="40vw" bg="purple.400" opacity="0.15" rounded="full" filter="blur(100px)" />

      <Box 
        w="100%" 
        maxW="900px" 
        bg={useColorModeValue('whiteAlpha.900', 'gray.800')} 
        backdropFilter="blur(20px)"
        border="1px solid"
        borderColor={useColorModeValue('whiteAlpha.200', 'whiteAlpha.100')}
        borderRadius="3xl"
        shadow="2xl"
        p={{ base: 8, md: 16 }}
        position="relative"
        zIndex={1}
      >
        <VStack spacing={8} align="stretch" dir="rtl">
          <VStack spacing={4} align="center" mb={6}>
            <Box p={5} bgGradient="linear(to-br, blue.400, purple.500)" color="white" borderRadius="2xl" shadow="xl" transform="rotate(-5deg)">
              <Icon as={MdVideoCall} w={12} h={12} />
            </Box>
            <Heading size="2xl" color={headingColor} textAlign="center" fontWeight="black" letterSpacing="tight">
              إنشاء حصة مباشرة جديدة
            </Heading>
            <Text color={textColor} fontSize="xl" textAlign="center" maxW="600px">
              اختر المادة والمجموعة لإنشاء غرفة افتراضية فورية. سيتمكن طلابك من الانضمام مباشرة وبكل سهولة.
            </Text>
          </VStack>

          {createdMeetingInfo ? (
            <Box p={8} bg="green.50" borderRadius="2xl" borderWidth="1px" borderColor="green.200" dir="rtl" shadow="inner">
              <VStack spacing={6} align="stretch">
                <Heading size="lg" color="green.700" textAlign="center">
                  🎉 تم إنشاء الحصة بنجاح!
                </Heading>
                <Text color="green.600" textAlign="center" fontSize="lg">
                  شارك هذه التفاصيل مع طلابك لتمكينهم من الانضمام:
                </Text>
                
                <Flex gap={6} direction={{ base: "column", md: "row" }}>
                  <Box flex={1} bg="white" p={6} borderRadius="xl" shadow="md" borderWidth="1px" textAlign="center">
                    <Text fontSize="sm" color="gray.500" mb={2} fontWeight="bold">رقم الحصة (Meeting ID)</Text>
                    <Text fontSize="3xl" fontWeight="black" letterSpacing="widest" color="blue.600">
                      {createdMeetingInfo.meetingId}
                    </Text>
                  </Box>
                  <Box flex={1} bg="white" p={6} borderRadius="xl" shadow="md" borderWidth="1px" textAlign="center">
                    <Text fontSize="sm" color="gray.500" mb={2} fontWeight="bold">الرقم السري (Secret Key)</Text>
                    <Text fontSize="3xl" fontWeight="black" letterSpacing="widest" color="red.500">
                      {createdMeetingInfo.secretKey}
                    </Text>
                  </Box>
                </Flex>

                <Button 
                  size="lg"
                  colorScheme="gray" 
                  variant="outline"
                  onClick={() => setCreatedMeetingInfo(null)}
                  mt={4}
                  borderRadius="xl"
                  h="60px"
                  fontSize="lg"
                >
                  إنشاء حصة أخرى
                </Button>
              </VStack>
            </Box>
          ) : (
            <VStack spacing={8} dir="rtl" align="stretch">
              <Flex gap={8} flexDir={{ base: "column", md: "row" }}>
                <Box flex={1}>
                  <Text mb={3} fontSize="lg" fontWeight="bold" color={headingColor}>المادة الدراسية</Text>
                  <ReactSelect
                    placeholder="اختر المادة..."
                    value={selectedMatiere ? { label: selectedMatiere, value: selectedMatiere } : null}
                    onChange={(selected) => {
                      setSelectedMatiere(selected ? selected.value : "");
                      setSelectedGroups([]); 
                    }}
                    options={matieresList.map((matier) => ({ label: matier, value: matier }))}
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: "1rem",
                        minHeight: "64px",
                        borderColor: state.isFocused ? "#3182CE" : borderColor,
                        backgroundColor: inputBg,
                        fontSize: "1.1rem",
                        boxShadow: state.isFocused ? "0 0 0 1px #3182CE" : "none",
                        "&:hover": {
                          borderColor: "#3182CE"
                        }
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </Box>

                <Box flex={1}>
                  <Text mb={3} fontSize="lg" fontWeight="bold" color={headingColor}>
                    المجموعات (يمكن اختيار أكثر من مجموعة)
                  </Text>
                  <ReactSelect
                    placeholder={currentGroups.length > 0 ? "اختر مجموعة أو أكثر..." : "لا توجد مجموعات متاحة"}
                    value={selectedGroups}
                    onChange={(selected) => setSelectedGroups(selected || [])}
                    options={currentGroups.map((group) => ({ label: group, value: group }))}
                    isDisabled={!selectedMatiere || currentGroups.length === 0}
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: "1rem",
                        minHeight: "64px",
                        borderColor: state.isFocused ? "#3182CE" : borderColor,
                        backgroundColor: inputBg,
                        fontSize: "1.1rem",
                        boxShadow: state.isFocused ? "0 0 0 1px #3182CE" : "none",
                        "&:hover": {
                          borderColor: "#3182CE"
                        }
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#ebf4ff",
                        borderRadius: "0.5rem",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#2b6cb0",
                        fontWeight: "600",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#2b6cb0",
                        ':hover': { backgroundColor: '#bee3f8', color: '#1a365d' },
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                  {selectedGroups.length > 0 && (
                    <Text mt={2} fontSize="sm" color="blue.600" fontWeight="medium">
                      ✓ تم اختيار {selectedGroups.length} مجموعة{selectedGroups.length > 1 ? '' : ''}
                    </Text>
                  )}
                </Box>
              </Flex>

              <Button 
                size="lg"
                colorScheme="blue" 
                bgGradient="linear(to-r, blue.400, blue.600)"
                onClick={handleCreateMeeting} 
                isLoading={loading}
                borderRadius="xl"
                h="70px"
                fontSize="2xl"
                fontWeight="black"
                boxShadow="0 10px 25px -5px rgba(49, 130, 206, 0.5)"
                _hover={{ 
                  transform: "translateY(-4px)", 
                  boxShadow: "0 15px 35px -5px rgba(49, 130, 206, 0.6)",
                  bgGradient: "linear(to-r, blue.500, blue.700)"
                }}
                transition="all 0.3s cubic-bezier(.25,.8,.25,1)"
                w="100%"
                mt={4}
              >
                بدء الحصة المباشرة الآن
              </Button>
            </VStack>
          )}
        </VStack>
      </Box>
    </Flex>
  );
}

